import {
  ChatOutlined,
  DeleteOutlineOutlined,
  FileDownloadOutlined,
  Refresh,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  type MRT_SortingState,
  type MRT_TableOptions,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import DeleteDialog from "src/reusable/components/DeleteDialog";
import DocumentStatusIndicator from "src/reusable/components/DocumentStatusIndicator";
import { NucToast } from "src/reusable/components/Toast";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import {
  useDeleteSDRById,
  useReprocessSDRById,
} from "../services/DatasetServices";
import { useCanDeleteDatasetRecords } from "./useCanDeleteDatasetRecords";
import { useCanReprocessDatasetRecords } from "./useCanReprocessDatasetRecords";
import {
  useDownloadDocumentPreview,
  useGetSDRArtifacts,
} from "../services/DocumentServices";
import { ProcessingStatusData, SourceDataRecord } from "../types";
import { SDRImage } from "./SDRImage";
import { SourceMetadataSummary } from "./SourceMetadata";
import { prettifyColumnName } from "./datasetColumns";

interface SDRTableProps {
  records: SourceDataRecord[];
  isDocumentDataset: boolean;
  onRowClick: (record: SourceDataRecord) => void;
  totalCount: number;
  isEstimateCount?: boolean;

  // Pagination (controlled)
  pagination: MRT_PaginationState;
  onPaginationChange: MRT_TableOptions<SourceDataRecord>["onPaginationChange"];

  // Sorting (controlled)
  sorting: MRT_SortingState;
  onSortingChange: MRT_TableOptions<SourceDataRecord>["onSortingChange"];

  isLoading: boolean;
  isFetching?: boolean;

  /** Dynamic columns for tabular datasets */
  dynamicColumns?: string[];

  /** Processing status map for showing detailed status per record */
  processingStatusMap?: Map<number, ProcessingStatusData>;

  /** Callback when a record is reprocessed (to trigger status polling) */
  onReprocess?: (sdrId: number) => void;
}

// Helper to format cell values nicely
const formatCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// Pre-computed date format options (avoid recreating on each call)
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

// Format date helper
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);
};

// Pre-computed styles for tabular cells (avoid creating new objects each render)
const CELL_STYLES = {
  index: {
    color: "#9e9e9e",
    fontFamily: "monospace",
    fontWeight: 500,
  } as const,
  date: { color: "#757575" } as const,
  cellBase: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
  } as const,
  cellEmpty: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
    color: "#9e9e9e",
  } as const,
};

// Pre-computed container styles
const CONTAINER_SX = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
} as const;

// Pre-computed MRT configuration objects (avoid recreating on each render)
const PAGINATION_PROPS = {
  color: "primary" as const,
  shape: "rounded" as const,
  showRowsPerPage: true,
  rowsPerPageOptions: [25, 50, 100],
};

const BOTTOM_TOOLBAR_PROPS = {
  sx: {
    minHeight: 48,
    py: 0.5,
    px: 2,
    borderTop: "1px solid #e0e0e0",
  },
};

const TABLE_CONTAINER_PROPS = {
  sx: {
    flex: 1,
    minHeight: 0,
    maxWidth: "100%",
    width: "100%",
    overflowX: "auto",
    // GPU acceleration hints for smoother scrolling
    willChange: "scroll-position",
    transform: "translateZ(0)",
    "&::-webkit-scrollbar": {
      width: 8,
      height: 8,
    },
    "&::-webkit-scrollbar-track": {
      bgcolor: "#f5f5f5",
    },
    "&::-webkit-scrollbar-thumb": {
      bgcolor: "#e0e0e0",
      "&:hover": {
        bgcolor: "#bdbdbd",
      },
    },
  },
};

const TABLE_PAPER_PROPS = {
  elevation: 0,
  sx: {
    border: "1px solid #e0e0e0",
    borderRadius: 1,
    overflow: "hidden",
    height: "100%",
    width: "100%",
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
  },
};

const COLUMN_VIRTUALIZER_OPTIONS = {
  overscan: 10, // Higher overscan for smoother scrolling with many columns
  estimateSize: () => 180,
};

// Fixed column order for document datasets
const DOCUMENT_COLUMN_ORDER = [
  "thumbnail",
  "file_name",
  "author",
  "document_type",
  "version_number",
  "create_date",
  "actions",
];

const DOCUMENT_COLUMN_PINNING = { left: ["thumbnail"], right: ["actions"] };
const TABULAR_COLUMN_PINNING = { right: ["actions"] };

// Suppress the inset box-shadow MRT adds to pinned column edges via ::before.
// MRT nests the shadow under &[data-pinned="true"], so we must match that specificity.
const PINNED_SHADOW_OVERRIDE = {
  sx: { '&[data-pinned="true"]': { "&:before": { boxShadow: "none" } } },
};

// Pre-computed empty state (static component, no need to recreate)
const EMPTY_ROWS_FALLBACK = (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 8,
      textAlign: "center",
    }}
  >
    <Typography variant="h6" color="text.secondary" gutterBottom>
      No records found
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Try adjusting your search or filters
    </Typography>
  </Box>
);

// Static function reference for empty fallback (avoids function recreation)
const renderEmptyFallback = () => EMPTY_ROWS_FALLBACK;

// Pre-computed localization
const LOCALIZATION = {
  noRecordsToDisplay: "No records found",
};

// Stable getRowId function (avoids recreation per render)
const getRowId = (row: SourceDataRecord) => String(row.id);

// Action column sizing — derived from MUI IconButton dimensions so the column
// width stays in sync with the actual button count.
const MUI_ICON_BUTTON_SM = 34; // MUI IconButton size="small" rendered width
const ACTION_STACK_GAP = 4; // Stack spacing={0.5} in px
const ACTION_CELL_PADDING = 16; // 8px padding each side

const DOCUMENT_ACTION_COUNT = 4; // chat, download, reprocess, delete
const TABULAR_ACTION_COUNT = 1; // delete only

const DOCUMENT_ACTIONS_WIDTH =
  DOCUMENT_ACTION_COUNT * MUI_ICON_BUTTON_SM +
  (DOCUMENT_ACTION_COUNT - 1) * ACTION_STACK_GAP +
  ACTION_CELL_PADDING; // 164

const TABULAR_ACTIONS_WIDTH =
  TABULAR_ACTION_COUNT * MUI_ICON_BUTTON_SM + ACTION_CELL_PADDING; // 50

// Lightweight delete-only actions for tabular datasets (avoids unnecessary hooks)
const TabularRowActions = ({ record }: { record: SourceDataRecord }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Requires the dataset:delete capability AND write on this dataset —
  // same two-axis decision the delete endpoint enforces.
  const canDeleteSdr = useCanDeleteDatasetRecords(record.dataset_fk);
  const {
    mutate: deleteSDRById,
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
  } = useDeleteSDRById();

  useEffect(() => {
    if (isDeleteSuccess) {
      NucToast({ message: "Deleted Dataset record", type: "success" });
    } else if (isDeleteError) {
      NucToast({ message: "Error deleting Dataset record", type: "error" });
    }
  }, [isDeleteSuccess, isDeleteError]);

  return (
    <>
      <Tooltip
        title={
          canDeleteSdr
            ? "Delete"
            : "You do not have permission to delete records from this dataset"
        }
      >
        <span style={{ display: "inline-flex" }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteDialogOpen(true);
            }}
            color="error"
            disabled={!canDeleteSdr}
            data-testid={`sdr-btn-delete-${record.id}`}
          >
            <DeleteOutlineOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete Source Data Record"
        content="This is a destructive action. Are you sure you want to delete this source data record?"
        onDelete={() => {
          deleteSDRById(record.id);
          setDeleteDialogOpen(false);
        }}
        isLoading={isDeleteLoading}
        isSuccess={isDeleteSuccess}
        confirmButtonText="Delete"
      />
    </>
  );
};

// Full row action buttons component (only used for document datasets)
/**
 * The per-row action buttons. Exported so the permission gates on Reprocess
 * and Delete can be asserted at the same granularity `SDRCard`'s menu already
 * is — rendering the whole table to reach two IconButtons would test
 * MaterialReactTable instead.
 */
export const RowActions = ({
  record,
  onReprocessCallback,
}: {
  record: SourceDataRecord;
  onReprocessCallback?: (sdrId: number) => void;
}) => {
  const history = useHistory();
  const { setSourceDataRecordIds } = useChatStore();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reprocessDialogOpen, setReprocessDialogOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    mutate: deleteSDRById,
    isLoading: isDeleteLoading,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
  } = useDeleteSDRById();

  // Requires the dataset:delete capability AND write on this dataset —
  // same two-axis decision the delete endpoint enforces.
  const canDeleteSdr = useCanDeleteDatasetRecords(record.dataset_fk);
  // Reprocessing deletes and rebuilds the document's chunks and embeddings,
  // so the backend write-gates it per object exactly as delete is gated
  // (frontend#2254). Without this a read-only grantee sees a greyed-out
  // Delete beside a live Reprocess on the same row, and confirming 403s.
  const canReprocessSdr = useCanReprocessDatasetRecords(record.dataset_fk);

  const {
    mutate: reprocessSDR,
    isLoading: isReprocessLoading,
    isSuccess: isReprocessSuccess,
    reset: resetReprocess,
  } = useReprocessSDRById();

  const { data: artifacts } = useGetSDRArtifacts(record.id);
  const artifactId =
    artifacts && artifacts.length > 0 ? artifacts[0]?.id : null;
  const { refetch: downloadDocument } = useDownloadDocumentPreview(artifactId);

  useEffect(() => {
    if (isDeleteSuccess) {
      NucToast({ message: "Deleted Dataset record", type: "success" });
    } else if (isDeleteError) {
      NucToast({ message: "Error deleting Dataset record", type: "error" });
    }
  }, [isDeleteSuccess, isDeleteError]);

  // Notify parent when reprocess succeeds to trigger status polling
  useEffect(() => {
    if (isReprocessSuccess && onReprocessCallback) {
      onReprocessCallback(record.id);
    }
  }, [isReprocessSuccess, onReprocessCallback, record.id]);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!artifactId) return;

    setIsDownloading(true);
    try {
      const { data } = await downloadDocument();
      if (data) {
        const url = URL.createObjectURL(data);
        const fileName = record.data.file_name || `document-${record.id}.pdf`;
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error downloading document:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleChat = (e: React.MouseEvent) => {
    e.stopPropagation();
    const fileName = record.data?.file_name || `Document ${record.id}`;

    // Scope the chat to the record alone. Selecting its dataset too would
    // search the whole dataset, since selected datasets and records union.
    setSourceDataRecordIds([record.id]);

    history.push({
      pathname: "/assistant",
      state: {
        preloadDocument: {
          sdrId: record.id,
          fileName: fileName,
          datasetId: record.dataset_fk,
        },
      },
    });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteSDRById(record.id);
    setDeleteDialogOpen(false);
  };

  const handleReprocess = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetReprocess(); // Reset mutation state so dialog can open again after previous success
    setReprocessDialogOpen(true);
  };

  const handleReprocessConfirm = () => {
    reprocessSDR(record.id);
    setReprocessDialogOpen(false);
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
      >
        <Tooltip title="Chat with Document">
          <IconButton
            size="small"
            onClick={handleChat}
            color="primary"
            data-testid={`sdr-btn-chat-${record.id}`}
          >
            <ChatOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <span>
            <IconButton
              size="small"
              onClick={handleDownload}
              disabled={isDownloading || !artifactId}
              data-testid={`sdr-btn-download-${record.id}`}
            >
              <FileDownloadOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={
            canReprocessSdr
              ? "Reprocess Document"
              : "You do not have permission to reprocess records in this dataset"
          }
        >
          <span>
            <IconButton
              size="small"
              onClick={handleReprocess}
              disabled={!canReprocessSdr || isReprocessLoading}
              color="warning"
              data-testid={`sdr-reprocess-${record.id}`}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={
            canDeleteSdr
              ? "Delete"
              : "You do not have permission to delete records from this dataset"
          }
        >
          <span style={{ display: "inline-flex" }}>
            <IconButton
              size="small"
              onClick={handleDelete}
              color="error"
              disabled={!canDeleteSdr}
              data-testid={`sdr-btn-delete-${record.id}`}
            >
              <DeleteOutlineOutlined fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title="Delete Source Data Record"
        content="This is a destructive action. Are you sure you want to delete this source data record?"
        onDelete={handleDeleteConfirm}
        isLoading={isDeleteLoading}
        isSuccess={isDeleteSuccess}
        confirmButtonText="Delete"
      />

      <DeleteDialog
        open={reprocessDialogOpen}
        onClose={() => setReprocessDialogOpen(false)}
        title="Reprocess Document"
        content="This will clear all processed data (OCR, chunks, embeddings) and re-run the full processing pipeline. The document will be unavailable for search until processing completes."
        onDelete={handleReprocessConfirm}
        isLoading={isReprocessLoading}
        isSuccess={isReprocessSuccess}
        confirmButtonText="Reprocess"
      />
    </>
  );
};

// Document info cell component (preserves existing card layout)
export const DocumentInfoCell = ({
  record,
  processingStatus,
}: {
  record: SourceDataRecord;
  processingStatus?: ProcessingStatusData;
}) => {
  return (
    <Box sx={{ minWidth: 130 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography
          variant="body1"
          data-testid={`sdr-filename-${record.id}`}
          sx={{
            fontWeight: 600,
            color: "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {record.data?.file_name || `Record ${record.id}`}
        </Typography>
        <DocumentStatusIndicator
          recordId={record.id}
          chunksWithEmbedding={record.chunks_with_embedding}
          processingStatus={processingStatus}
          variant="full"
          showChunkProgress={true}
        />
      </Stack>
      {record.data?.extracted_metadata?.title && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 0.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {record.data.extracted_metadata.title}
        </Typography>
      )}
      {record.data?.extracted_metadata?.description && (
        <Typography
          variant="caption"
          sx={{
            color: grey[500],
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.4,
          }}
        >
          {record.data.extracted_metadata.description}
        </Typography>
      )}
      <Box sx={{ mt: 0.75 }}>
        <SourceMetadataSummary data={record.data} recordId={record.id} />
      </Box>
    </Box>
  );
};

/** Tiny component so the row index re-renders on page change without
 *  recreating the entire column definition array (290+ columns). */
const RowIndexCell = ({
  rowIndex,
  pagination,
}: {
  rowIndex: number;
  pagination: MRT_PaginationState;
}) => (
  <span style={CELL_STYLES.index}>
    {rowIndex + 1 + pagination.pageIndex * pagination.pageSize}
  </span>
);

const SDRTable = ({
  records,
  isDocumentDataset,
  onRowClick,
  totalCount,
  isEstimateCount = false,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  isLoading,
  isFetching = false,
  dynamicColumns = [],
  processingStatusMap,
  onReprocess,
}: SDRTableProps) => {
  const theme = useTheme();

  // Pre-compute row styles to avoid creating objects on each row render
  const rowSx = useMemo(
    () =>
      isDocumentDataset
        ? {
            cursor: "pointer",
            height: 200,
            transition: "background-color 0.15s ease",
            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
          }
        : { cursor: "pointer", height: 52 },
    [isDocumentDataset, theme.palette.primary.main],
  );

  // Document dataset columns
  const documentColumns = useMemo<MRT_ColumnDef<SourceDataRecord>[]>(
    () => [
      {
        id: "thumbnail",
        header: "",
        size: 150,
        minSize: 150,
        maxSize: 150,
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        enablePinning: true,
        muiTableBodyCellProps: {
          sx: {
            px: 1,
            py: 1.5,
            textAlign: "center",
          },
        },
        Cell: ({ row }) => (
          <Box
            sx={{
              width: 130,
              height: 170,
              mx: "auto",
              bgcolor: grey[100],
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
              border: `1px solid ${grey[200]}`,
            }}
          >
            <SDRImage sdrID={row.original.id} />
          </Box>
        ),
      },
      {
        accessorKey: "data.file_name",
        id: "file_name",
        header: "Document",
        size: 200,
        minSize: 150,
        enableSorting: true,
        Cell: ({ row }) => (
          <DocumentInfoCell
            record={row.original}
            processingStatus={processingStatusMap?.get(row.original.id)}
          />
        ),
      },
      {
        accessorFn: (row) => row.data?.extracted_metadata?.author || "-",
        id: "author",
        header: "Author",
        size: 100,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorFn: (row) => row.data?.extracted_metadata?.document_type || "",
        id: "document_type",
        header: "Type",
        size: 100,
        enableSorting: true,
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return value ? (
            <Chip
              label={value}
              size="small"
              sx={{
                height: 24,
                fontSize: "0.75rem",
                bgcolor: alpha(blue[500], 0.08),
                color: blue[700],
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        },
      },
      {
        accessorKey: "version_number",
        id: "version_number",
        header: "Rev",
        size: 50,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontFamily: "monospace",
            }}
          >
            {cell.getValue<number>() ?? "-"}
          </Typography>
        ),
      },
      {
        accessorKey: "create_date",
        id: "create_date",
        header: "Created",
        size: 90,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {formatDate(cell.getValue<string>())}
          </Typography>
        ),
      },
      {
        id: "actions",
        header: "",
        size: DOCUMENT_ACTIONS_WIDTH,
        minSize: DOCUMENT_ACTIONS_WIDTH,
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        muiTableHeadCellProps: PINNED_SHADOW_OVERRIDE,
        muiTableBodyCellProps: PINNED_SHADOW_OVERRIDE,
        Cell: ({ row }) => (
          <Box onClick={(e) => e.stopPropagation()}>
            <RowActions
              record={row.original}
              onReprocessCallback={onReprocess}
            />
          </Box>
        ),
      },
    ],
    [theme, processingStatusMap, onReprocess],
  );

  // Tabular dataset columns (dynamic based on data)
  const tabularColumns = useMemo<MRT_ColumnDef<SourceDataRecord>[]>(() => {
    const cols: MRT_ColumnDef<SourceDataRecord>[] = [
      {
        id: "index",
        header: "#",
        size: 50,
        minSize: 50,
        maxSize: 50,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <RowIndexCell rowIndex={row.index} pagination={pagination} />
        ),
      },
    ];

    // Add dynamic columns - simplified cell renderer for performance with many columns
    dynamicColumns.forEach((col) => {
      cols.push({
        accessorFn: (row) => row.data?.[col],
        id: col,
        header: prettifyColumnName(col),
        size: 180,
        minSize: 100,
        enableSorting: true,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const value = cell.getValue<unknown>();
          const displayValue = formatCellValue(value);
          return (
            <span
              style={
                displayValue === "-"
                  ? CELL_STYLES.cellEmpty
                  : CELL_STYLES.cellBase
              }
              title={displayValue}
            >
              {displayValue}
            </span>
          );
        },
      });
    });

    // Add date column
    cols.push({
      accessorKey: "create_date",
      id: "create_date",
      header: "Created",
      size: 100,
      minSize: 100,
      enableSorting: true,
      enableColumnActions: false,
      Cell: ({ cell }) => (
        <span style={CELL_STYLES.date}>
          {formatDate(cell.getValue<string>())}
        </span>
      ),
    });

    // Add actions column - using lightweight TabularRowActions (fewer hooks)
    cols.push({
      id: "actions",
      header: "",
      size: TABULAR_ACTIONS_WIDTH,
      minSize: TABULAR_ACTIONS_WIDTH,
      maxSize: TABULAR_ACTIONS_WIDTH,
      enableSorting: false,
      enableColumnActions: false,
      muiTableHeadCellProps: PINNED_SHADOW_OVERRIDE,
      muiTableBodyCellProps: PINNED_SHADOW_OVERRIDE,
      Cell: ({ row }) => <TabularRowActions record={row.original} />,
    });

    return cols;
    // Note: pagination.pageIndex/pageSize intentionally NOT in deps - Cell captures from closure
    // This prevents recreating 290 column definitions on every page change
  }, [dynamicColumns]);

  const columns = isDocumentDataset ? documentColumns : tabularColumns;

  // Dynamic column order for tabular datasets (based on actual columns)
  const tabularColumnOrder = useMemo(() => {
    return tabularColumns.map(
      (col) => col.id || (col.accessorKey as string) || "",
    );
  }, [tabularColumns]);

  // Pre-compute header cell styles (only theme-dependent part)
  const headerCellSx = useMemo(
    () => ({
      bgcolor: theme.palette.mode === "dark" ? grey[900] : grey[50],
      fontWeight: 600,
      fontSize: "0.8125rem",
      color: grey[700],
      borderBottom: `1px solid ${grey[300]}`,
      py: 1,
      whiteSpace: "nowrap",
      "& .Mui-TableHeadCell-Content": {
        justifyContent: "space-between",
      },
    }),
    [theme.palette.mode],
  );

  // Pre-compute body cell styles (only isDocumentDataset-dependent)
  const bodyCellSx = useMemo(
    () => ({
      py: isDocumentDataset ? 1.5 : 1,
      verticalAlign: isDocumentDataset ? "top" : "middle",
    }),
    [isDocumentDataset],
  );

  // Memoize the prop objects themselves to avoid creating new refs
  const headerCellProps = useMemo(() => ({ sx: headerCellSx }), [headerCellSx]);
  const bodyCellProps = useMemo(() => ({ sx: bodyCellSx }), [bodyCellSx]);

  // Memoized row props callback - avoids recreating function references on each render
  const getRowProps = useCallback(
    ({ row }: { row: { original: SourceDataRecord } }) => ({
      onClick: () => onRowClick(row.original),
      sx: rowSx,
    }),
    [onRowClick, rowSx],
  );

  const table = useMaterialReactTable({
    columns,
    data: records,
    getRowId,

    // Server-side features
    manualPagination: true,
    manualSorting: true,
    // When count is an estimate (100,000+), MRT uses this for page count math.
    // We add -1 to signal "unknown total" which disables "last page" button.
    rowCount: isEstimateCount ? -1 : totalCount,

    // Controlled state - includes columnOrder to enforce column positioning
    state: {
      pagination,
      sorting,
      isLoading,
      showProgressBars: isFetching && !isLoading,
      columnOrder: isDocumentDataset
        ? DOCUMENT_COLUMN_ORDER
        : tabularColumnOrder,
      columnPinning: isDocumentDataset
        ? DOCUMENT_COLUMN_PINNING
        : TABULAR_COLUMN_PINNING,
    },
    onPaginationChange,
    onSortingChange,

    // Row click handling - using memoized callback
    muiTableBodyRowProps: getRowProps,

    // Styling - static booleans
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableColumnPinning: true,
    enableRowSelection: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,

    // Column virtualization for better horizontal scroll performance
    enableColumnVirtualization: true,
    columnVirtualizerOptions: COLUMN_VIRTUALIZER_OPTIONS,
    enableSortingRemoval: true,
    enableTopToolbar: false,
    enableBottomToolbar: true,
    enablePagination: true,
    paginationDisplayMode: "pages",
    layoutMode: "grid",

    // Pre-computed static props
    muiPaginationProps: PAGINATION_PROPS,
    muiBottomToolbarProps: BOTTOM_TOOLBAR_PROPS,
    muiTableContainerProps: TABLE_CONTAINER_PROPS,
    muiTablePaperProps: TABLE_PAPER_PROPS,

    // Memoized theme-dependent props
    muiTableHeadCellProps: headerCellProps,
    muiTableBodyCellProps: bodyCellProps,

    // Pre-computed empty state and localization
    renderEmptyRowsFallback: renderEmptyFallback,
    localization: LOCALIZATION,
  });

  return (
    <Box data-testid="sdr-table-container" sx={CONTAINER_SX}>
      <MaterialReactTable table={table} />
    </Box>
  );
};

export default SDRTable;
